import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Skeleton } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { DefaultProps } from "../LayoutWrapper";

interface ILogItem {
    log: string;
    date: Date;
}

interface State {
    showModal: boolean;
    inProgress: boolean;
    logs: ILogItem[];
}

interface Props extends DefaultProps {
    dataLoaded: boolean;
    playerData: any;
}

class ExportPlayerWrapped extends React.Component<Props, State> {

    constructor(props) {
        super(props);

        this.state = {
            showModal: false,
            inProgress: false,
            logs: [],
        };
    }

    componentDidMount() {
        if (this.props.socket) {
            this.props.socket.on("PlayerExportUpdate", data => {
                if (data.exporterUserId === this.props.activeUser.userId) {
                    if (data.finished === true) {
                        this.setState({
                            inProgress: false,
                        });
                    }

                    this.setState({ logs: [...this.state.logs, {
                            log: data.log,
                            date: new Date(),
                        }]
                    });
                }
            });
        }
    }

    showModal = () => {
        this.setState({ showModal: true });
    }

    hideModal = () => {
        this.setState({ showModal: false });
    }

    cancel = () => {
        this.setState({ showModal: false, logs: [], inProgress: false });
        this.props.form.resetFields();
    }

    send = () => {
        this.setState({ inProgress: true });

        const data = {
            exporterUserId: this.props.activeUser.userId,
        };

        axios.post(`${Config.getInstance.apiUrl}/api/user/${this.props.playerData.id}/export`, data,
            {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
                responseType: "blob",
            })
            .then(response => {
                this.setState({ inProgress: false, logs: [], showModal: false });

                const url: string = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `user_data_${this.props.playerData.id}.zip`);
                document.body.appendChild(link);
                link.click();
            })
            .catch(_ => {
                this.setState({ inProgress: false, logs: [] });
                PopupMessage.error("Something went wrong!");
            });
    }

    render() {
        const { classes } = this.props;

        if (!this.props.dataLoaded) {
            return <Skeleton width={110} height={55}/>
        }

        return (
            <div>
                <Button disableElevation variant="contained" size="small" onClick={this.showModal}>
                    Export player data
                </Button>

                <Dialog
                    open={this.state.showModal}
                    fullWidth={true}
                    maxWidth="sm"
                    onClose={(_, reason) => {
                        if (reason !== "backdropClick") {
                            this.hideModal();
                        }
                    }}
                >
                    <DialogTitle>
                        Export user data
                    </DialogTitle>

                    <Alert severity="warning">
                        Data is exported from the database. Cached data is not exported!<br/><br/>
                        Any data that links to other users are not exported! For example: messages, battle replies, alliance etc.
                    </Alert>

                    <DialogContent dividers>
                        {this.state.inProgress && <React.Fragment>
                            <LinearProgress />

                            <div className="fakeScreen">
                                {this.state.logs.map(log => <p className="line">
                                    <span style={{ paddingRight: 10 }}>
                                        [{Helpers.formatDateTime(log.date || new Date(), true)}]
                                    </span>

                                    {log.log}
                                </p>)}
                            </div>
                        </React.Fragment>}
                    </DialogContent>

                    <DialogActions>
                        <Button disableElevation
                            onClick={this.cancel}
                            disabled={this.state.inProgress}
                            variant="contained"
                        >
                            Cancel
                        </Button>

                        <div className={classes.wrapper}>
                            <Button disableElevation
                                onClick={this.send}
                                color="primary"
                                variant="contained"
                                className={classes.loadingButton}
                                disabled={this.state.inProgress}
                            >
                                Export
                            </Button>

                            {this.state.inProgress && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

const styles: any = theme => ({
    loadingButton: {
        width: "100%",
    },
    wrapper: {
        margin: 8,
        position: "relative",
        marginTop: 10,
        width: 150,
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
});
export const ExportPlayer = withStyles(styles)(ExportPlayerWrapped);
